import { Transaction } from "@powersync/web"
import { powerSyncDb } from "./db"
import * as Sentry from "@sentry/react"

/**
 * Serialize a value for SQLite storage.
 * Objects/arrays are JSON-stringified; primitives pass through.
 */
function toSqliteValue(value: unknown): unknown {
  if (value === null || value === undefined) return null
  if (typeof value === "object") return JSON.stringify(value)
  return value
}

/**
 * Insert a row into a PowerSync table.
 * Uses INSERT OR REPLACE for idempotent retry safety.
 */
export async function psInsert(
  table: string,
  data: Record<string, unknown>,
  tx?: Transaction,
): Promise<void> {
  const columns = Object.keys(data)
  const placeholders = columns.map(() => "?").join(", ")
  const values = columns.map((k) => toSqliteValue(data[k]))
  const db = tx ? tx : powerSyncDb
  try {
    await db.execute(
      `INSERT OR REPLACE INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`,
      values,
    )
  } catch (err) {
    console.error("PowerSync insert failed:", err)
    Sentry.captureException(err)
    throw err
  }
}

/**
 * Update a row in a PowerSync table by ID.
 */
export async function psUpdate(
  table: string,
  id: string,
  data: Record<string, unknown>,
  tx?: Transaction,
): Promise<void> {
  const entries = Object.entries(data).filter(([key]) => key !== "id")
  if (entries.length === 0) return

  const setClauses = entries.map(([key]) => `${key} = ?`).join(", ")
  const values = entries.map(([, val]) => toSqliteValue(val))

  const db = tx ? tx : powerSyncDb
  await db.execute(`UPDATE ${table} SET ${setClauses} WHERE id = ?`, [
    ...values,
    id,
  ])
}

/**
 * Delete a row from a PowerSync table by ID.
 */
export async function psDelete(table: string, id: string): Promise<void> {
  await powerSyncDb.execute(`DELETE FROM ${table} WHERE id = ?`, [id])
}
